import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Button } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { CopilotContextComposerService } from '@brighthub/copilot';
import {
  FilterChipGroupComponent,
  FilterChipOption,
} from '../shared/filter-chip-group/filter-chip-group.component';
import { ColResizeDirective } from './col-resize.directive';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ClientSessionService } from '@brighthub/uisession';
import { ClientActionsStateService } from './client-actions-state.service';
import { FaTasksStateService } from './fa-tasks-state.service';
import { FaTasksComponent } from './fa-tasks.component';
import { OutcomesComponent } from './outcomes.component';
import { OutcomesStateService } from './outcomes-state.service';
import {
  ActionStatus,
  ActionsState,
  ClientAction,
  Opportunity,
  OpportunityStatus,
} from './client-actions.model';
import { OpportunityDetailsDialogComponent, OpportunityDialogResult } from './opportunity-details-dialog/opportunity-details-dialog.component';
import { OpportunityAddDialogComponent } from './opportunity-add-dialog/opportunity-add-dialog.component';
import { ClientActionDetailsDialogComponent, ClientActionDialogResult } from './client-action-details-dialog/client-action-details-dialog.component';
import { ClientActionAddDialogComponent } from './client-action-add-dialog/client-action-add-dialog.component';

const EMPTY_STATE: ActionsState = {
  opportunities: [],
  actions: [],
};

const ALL_OPP_STATUSES: OpportunityStatus[] = [
  'Potential',
  'Committed',
  'Completed',
  'Withdrawn',
];
const ALL_ACTION_STATUSES: ActionStatus[] = [
  'To Do',
  'In Progress',
  'Done',
  'Blocked',
  'Withdrawn',
];

const OPPORTUNITY_FILTER_OPTIONS: FilterChipOption[] = ALL_OPP_STATUSES.map(
  (status) => ({
    id: status,
    label: status,
    ariaLabel: `Filter ${status} opportunities`,
  })
);

const ACTION_FILTER_OPTIONS: FilterChipOption[] = ALL_ACTION_STATUSES.map(
  (status) => ({
    id: status,
    label: status,
    ariaLabel: `Filter ${status} actions`,
  })
);

@Component({
  selector: 'bai-client-actions',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Button,
    ColResizeDirective,
    FaTasksComponent,
    FilterChipGroupComponent,
    OutcomesComponent,
    SkeletonModule,
    TagModule,
  ],
  providers: [DialogService, FaTasksStateService, OutcomesStateService],
  templateUrl: './client-actions.component.html',
  styleUrl: './client-actions.component.scss',
})
export class ClientActionsComponent {
  private _stateService = inject(ClientActionsStateService);
  private _clientSession = inject(ClientSessionService);
  private _dialogService = inject(DialogService);
  private _copilotComposer = inject(CopilotContextComposerService);
  private _dialogRef: DynamicDialogRef | null = null;

  readonly allOppStatuses = ALL_OPP_STATUSES;
  readonly allActionStatuses = ALL_ACTION_STATUSES;
  readonly opportunityFilterOptions = OPPORTUNITY_FILTER_OPTIONS;
  readonly actionFilterOptions = ACTION_FILTER_OPTIONS;

  private readonly _sessionId = computed(
    () => this._clientSession.selectedSession()?.uiSessionId ?? ''
  );

  readonly sessionId = this._sessionId;

  constructor() {
    effect(() => {
      const id = this._sessionId();
      if (id) this._stateService.ensureState(id);
    });
  }

  readonly state = computed(() => {
    const id = this._sessionId();
    if (!id) return EMPTY_STATE;
    return this._stateService.storeSnapshot()[id] ?? EMPTY_STATE;
  });

  /** Active status filters for the Opportunities table. */
  readonly opportunityFilter = signal<OpportunityStatus[]>([
    'Potential',
    'Committed',
  ]);

  /** Active status filters for the Actions table. */
  readonly actionFilter = signal<ActionStatus[]>(['To Do', 'In Progress']);

  readonly filteredOpportunities = computed(() =>
    this.state().opportunities.filter((o) =>
      this.opportunityFilter().includes(o.status)
    )
  );

  readonly filteredActions = computed(() =>
    this.state().actions.filter((a) => this.actionFilter().includes(a.status))
  );

  setOpportunityFilter(selection: string | string[]): void {
    const selected = Array.isArray(selection) ? selection : [selection];
    const next = selected.filter((status): status is OpportunityStatus =>
      this.allOppStatuses.includes(status as OpportunityStatus)
    );
    this.opportunityFilter.set(next);
  }

  toggleOppFilter(status: OpportunityStatus): void {
    this.opportunityFilter.update((current) =>
      current.includes(status)
        ? current.filter((item) => item !== status)
        : [...current, status]
    );
  }

  setActionFilter(selection: string | string[]): void {
    const selected = Array.isArray(selection) ? selection : [selection];
    const next = selected.filter((status): status is ActionStatus =>
      this.allActionStatuses.includes(status as ActionStatus)
    );
    this.actionFilter.set(next);
  }

  toggleActionFilter(status: ActionStatus): void {
    this.actionFilter.update((current) =>
      current.includes(status)
        ? current.filter((item) => item !== status)
        : [...current, status]
    );
  }

  prioritySeverity(priority: string): 'danger' | 'warn' | 'secondary' {
    if (priority === 'High') return 'danger';
    if (priority === 'Medium') return 'warn';
    return 'secondary';
  }

  statusSeverity(
    status: string
  ): 'success' | 'info' | 'secondary' | 'warn' | 'danger' {
    switch (status) {
      case 'Committed':
      case 'Done':
        return 'success';
      case 'In Progress':
        return 'info';
      case 'Potential':
      case 'To Do':
        return 'secondary';
      case 'Blocked':
        return 'warn';
      case 'Withdrawn':
      case 'Completed':
        return 'secondary';
      default:
        return 'secondary';
    }
  }

  opportunityLabel(opportunityId: string | null): string {
    if (!opportunityId) return '—';
    const opp = this.state().opportunities.find((o) => o.id === opportunityId);
    return opp ? opp.opportunityDescription : '—';
  }

  onApproveOpportunity(id: string): void {
    const sessionId = this._sessionId();
    if (sessionId) this._stateService.approveOpportunity(sessionId, id);
  }

  onWithdrawOpportunity(id: string): void {
    const sessionId = this._sessionId();
    if (sessionId) this._stateService.withdrawOpportunity(sessionId, id);
  }

  onApproveAction(id: string): void {
    const sessionId = this._sessionId();
    if (sessionId) this._stateService.approveAction(sessionId, id);
  }

  onWithdrawAction(id: string): void {
    const sessionId = this._sessionId();
    if (sessionId) this._stateService.withdrawAction(sessionId, id);
  }

  openOpportunityDetails(opp: Opportunity): void {
    this._dialogRef = this._dialogService.open(
      OpportunityDetailsDialogComponent,
      {
        header: 'Opportunity Details',
        width: '520px',
        data: opp,
      }
    );
    this._dialogRef.onClose.subscribe((result: OpportunityDialogResult | null) => {
      if (!result) return;
      const sessionId = this._sessionId();
      if (!sessionId) return;
      if (result.action === 'save') {
        this._stateService.updateOpportunity(sessionId, opp.id, result.patch);
      } else if (result.action === 'approve') {
        this._stateService.approveOpportunity(sessionId, opp.id);
      } else if (result.action === 'withdraw') {
        this._stateService.withdrawOpportunity(sessionId, opp.id);
      }
    });
  }

  openAddOpportunity(): void {
    this._dialogRef = this._dialogService.open(OpportunityAddDialogComponent, {
      header: 'Add Opportunity',
      width: '540px',
    });
    this._dialogRef.onClose.subscribe((result) => {
      if (result) {
        const sessionId = this._sessionId();
        if (sessionId) this._stateService.addOpportunity(sessionId, result);
      }
    });
  }

  openClientActionDetails(action: ClientAction): void {
    this._dialogRef = this._dialogService.open(
      ClientActionDetailsDialogComponent,
      {
        header: 'Action Details',
        width: '520px',
        data: { action, opportunities: this.state().opportunities },
      }
    );
    this._dialogRef.onClose.subscribe((result: ClientActionDialogResult | null) => {
      if (!result) return;
      const sessionId = this._sessionId();
      if (!sessionId) return;
      if (result.action === 'save') {
        this._stateService.updateAction(sessionId, action.id, result.patch);
      } else if (result.action === 'approve') {
        this._stateService.approveAction(sessionId, action.id);
      } else if (result.action === 'withdraw') {
        this._stateService.withdrawAction(sessionId, action.id);
      }
    });
  }

  openAddClientAction(): void {
    this._dialogRef = this._dialogService.open(ClientActionAddDialogComponent, {
      header: 'Add Client Action',
      width: '540px',
      data: { opportunities: this.state().opportunities },
    });
    this._dialogRef.onClose.subscribe((result) => {
      if (result) {
        const sessionId = this._sessionId();
        if (sessionId) this._stateService.addAction(sessionId, result);
      }
    });
  }

  addOpportunityToCopilot(opportunity: Opportunity): void {
    const preview = this.truncate(opportunity.opportunityDescription, 20);
    this._copilotComposer.addContext({
      sourceType: 'actions-opportunity',
      sourceId: opportunity.id,
      label: `Opportunity: ${preview}`,
      fullText:
        `[Actions | Opportunity | ${opportunity.id}] ` +
        `Description: ${opportunity.opportunityDescription}; ` +
        `Source: ${opportunity.source}; Source Type: ${opportunity.sourceType}; ` +
        `Priority: ${opportunity.priority}; Status: ${opportunity.status}; ` +
        `Created: ${opportunity.createdDate}; Updated: ${opportunity.updatedDate}`,
    });
  }

  addActionToCopilot(action: ClientAction): void {
    const preview = this.truncate(action.action, 20);
    this._copilotComposer.addContext({
      sourceType: 'actions-client-action',
      sourceId: action.id,
      label: `Client Action: ${preview}`,
      fullText:
        `[Actions | Client Action | ${action.id}] ` +
        `FA: ${action.financialAssistantName}; Action: ${action.action}; ` +
        `Opportunity: ${this.opportunityLabel(action.opportunityId)}; ` +
        `Source: ${action.source}; Source Type: ${action.sourceType}; ` +
        `Status: ${action.status}; Planned: ${action.plannedDate}; Deadline: ${action.deadline}`,
    });
  }

  truncate(value: string, max = 20): string {
    if (value.length <= max) return value;
    return `${value.slice(0, max)}…`;
  }
}
